import React, { useState } from 'react';
import {
  BackgroundCardWrapper,
  BackgroundName,
  CardImage,
  CardImageContainer,
  DeleteIconWrapper,
  SystemDefaultWrapper,
} from './styles';
import { Button, StatusBadge } from 'react-covideo-common';
import { Flex } from 'lib/components/styles/layout';
import { IoCheckmark } from 'react-icons/io5';
import { CSSProperties, useTheme } from 'styled-components/macro';
import { IBackgroundResponse } from 'lib/api/backgrounds/useQueryBackgrounds';
import { MdDeleteForever } from 'react-icons/md';
import { DeleteBackgroundModal } from './modals/DeleteBackgroundModal';
import { FormikHelpers } from 'formik';
import { ICreateSpotlightFormikValues } from '../../aiSpotlight/modal/types';
import { InlineTextEdit } from 'lib/components/InlineTextEdit';
import { useUpdateBackgroundMutation } from 'lib/api/backgrounds/useUpdateBackgroundMutation';
import { useTranslation } from 'react-i18next';

interface BackgroundCardProps {
  background: IBackgroundResponse;
  selectedBackground?: boolean;
  onDefaultBackgroundChange: (
    backgroundId: number,
    formikHelpers?: FormikHelpers<ICreateSpotlightFormikValues>
  ) => void;
  isLoading: boolean;
  selectedText: string;
  unselectedText: string;
  extendStyles?: {
    cardWrapperStyles?: CSSProperties;
  };
}

export const BackgroundCard = ({
  background,
  selectedBackground,
  onDefaultBackgroundChange,
  isLoading,
  selectedText,
  unselectedText,
  extendStyles,
}: BackgroundCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    fileName,
    default: isDefaultBackground,
    backgroundId,
    customerId,
  } = background;

  const defaultBackground =
    selectedBackground !== undefined ? selectedBackground : isDefaultBackground;

  // System generated backgrounds have null customerId
  const isSystemGeneratedBackground = customerId === null;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleShowDeleteModal = () => {
    setShowDeleteModal(prev => !prev);
  };

  const { mutateAsync: updateBackgroundMutation } =
    useUpdateBackgroundMutation();

  const handleSave = (fileName: string) => {
    updateBackgroundMutation({ id: backgroundId, fileName });
  };

  const buttonText = defaultBackground ? selectedText : unselectedText;
  const buttonVariant = defaultBackground ? 'passive' : 'white';
  const buttonIcon = defaultBackground ? (
    <IoCheckmark size={20} color={theme.colors.white[80]} />
  ) : undefined;

  const isProccessing = isLoading;
  const canDelete = customerId !== null; // only custom backgrounds can be deleted

  return (
    <BackgroundCardWrapper
      default={defaultBackground}
      style={extendStyles?.cardWrapperStyles}
    >
      {!isProccessing && canDelete && (
        <DeleteIconWrapper onClick={toggleShowDeleteModal}>
          <MdDeleteForever size={24} color={theme.colors.danger[100]} />
        </DeleteIconWrapper>
      )}
      {showDeleteModal && (
        <DeleteBackgroundModal
          background={background}
          onClose={toggleShowDeleteModal}
        />
      )}
      <Flex flexDirection='column' gap='10px'>
        <CardImageContainer>
          <CardImage
            src={background.url}
            width={280}
            height={165}
            loading='lazy'
            decoding='async'
          />
          {isSystemGeneratedBackground && (
            <SystemDefaultWrapper>
              <StatusBadge
                text={t('System Generated')}
                size='medium'
                extendStyles={{
                  container: {
                    backgroundColor: theme.colors.secondary['non_alpha'],
                    border: `1px solid ${theme.colors.secondary[5]}`,
                    borderRadius: '6px',
                  },
                  text: {
                    color: theme.colors.secondary[80],
                    fontWeight: 500,
                  },
                }}
              />
            </SystemDefaultWrapper>
          )}
        </CardImageContainer>
        <Flex
          flexDirection='column'
          padding='0 16px 16px 16px'
          gap='16px'
          flexGrow={1}
          justifyContent='space-between'
          width='auto'
        >
          <InlineTextEdit
            value={fileName}
            onSave={handleSave}
            viewComponent={BackgroundName}
            disabled={isLoading || isSystemGeneratedBackground}
          />
          <Button
            variant={buttonVariant}
            size='small'
            text={buttonText}
            icon={buttonIcon}
            disabled={isProccessing}
            onClick={() => onDefaultBackgroundChange(backgroundId)}
            extendStyles={{
              text: {
                fontWeight: 400,
              },
            }}
          />
        </Flex>
      </Flex>
    </BackgroundCardWrapper>
  );
};
